import axios, { AxiosResponse, AxiosErrorResponse } from '@/libs/axios';
import {
  useQuery,
  QueryObserverOptions,
  MutationOptions,
  useMutation
} from 'react-query';
import { VideoReelCommentModel } from '@/models';
import { QueryKey } from '@/layouts/shared';
import {
  CommentsQueryPayload,
  CommentsQueryResponse,
  CreateCommentPayload,
  CreateReplyCommentPayload,
  ReplyCommentsQueryPayload,
  ReplyCommentsQueryResponse,
  UpdateCommentPayload,
  UpdateReplyCommentPayload,
  VideoReelQueryResponse,
  VideoReelsQueryPayload,
  VideoReelsQueryResponse,
} from '@/features/reels/const';

const ApiUrl = {
  REEL: '/api/v1/reel',
  COMMENT: '/api/v1/reel/comment',
  COMMENT_REPLY: '/api/v1/reel/comment/reply',
};

export const fetchVideoReels = (params: string | VideoReelsQueryPayload) => {
  return axios.get(ApiUrl.REEL, { params });
};

export const getVideoDetail = (id: number) => {
  return axios.get(`${ApiUrl.REEL}/${id}`);
};

export const fetchComments = (reelId: number, params: any) => {
  return axios.get(`${ApiUrl.REEL}/${reelId}/comment`, { params });
};

export const fetchReplyComments = (commentId: number, params: any) => {
  return axios.get(`${ApiUrl.COMMENT}/${commentId}/reply`, { params });
};

export const createComment = ({ reelId, body }: CreateCommentPayload) => {
  return axios.post(`${ApiUrl.REEL}/${reelId}/comment`, body)
};

export const updateComment = ({ commentId, body }: UpdateCommentPayload) => {
  return axios.put(`${ApiUrl.COMMENT}/${commentId}`, body);
};

export const deleteComment = (commentId: number) => {
  return axios.delete(`${ApiUrl.COMMENT}/${commentId}`);
};

export const createReplyComment = ({ commentId, body }: CreateReplyCommentPayload) => {
  return axios.post(`${ApiUrl.COMMENT}/${commentId}/reply`, body)
};

export const updateReplyComment = ({ commentId, body }: UpdateReplyCommentPayload) => {
  return axios.put(`${ApiUrl.COMMENT_REPLY}/${commentId}`, body);
};

export const deleteReplyComment = (commentId: number) => {
  return axios.delete(`${ApiUrl.COMMENT_REPLY}/${commentId}`);
};

export const createLike = (reelId: number, body: any) => {
  return axios.post(`${ApiUrl.REEL}/${reelId}/reaction/LIKE`, body);
}

export const deleteLike = (reelId: number) => {
  return axios.delete(`${ApiUrl.REEL}/${reelId}/reaction/LIKE`);
}

export const trackReel = (reelId: number, params: any) => {
  return axios.get(`${ApiUrl.REEL}/${reelId}/tracking`, { params });
}

export const useFetchVideoReelsQuery = (
  payload: VideoReelsQueryPayload,
  options?: QueryObserverOptions<
    AxiosResponse<VideoReelsQueryResponse>,
    AxiosErrorResponse,
    VideoReelsQueryResponse,
    AxiosResponse<VideoReelsQueryResponse>,
    (string | VideoReelsQueryPayload)[]
  >
) => {
  return useQuery([QueryKey.Reel, payload], () => fetchVideoReels(payload), options);
};

export const useFetchVideoReelQuery = (
  payload: number,
  options?: QueryObserverOptions<
    AxiosResponse<VideoReelQueryResponse>,
    AxiosErrorResponse,
    VideoReelQueryResponse,
    AxiosResponse<VideoReelQueryResponse>,
    (string | number)[]
  >
) => {
  return useQuery([QueryKey.Reel, payload], () => getVideoDetail(payload), options);
};

export const useFetchCommentsQuery = (
  payload: CommentsQueryPayload,
  options?: QueryObserverOptions<
    AxiosResponse<CommentsQueryResponse>,
    AxiosErrorResponse,
    CommentsQueryResponse,
    AxiosResponse<CommentsQueryResponse>,
    (string | CommentsQueryPayload)[]
  >
) => {
  return useQuery([QueryKey.ReelComment, payload], () => fetchComments(payload.reelId, payload.params), options);
};

export const useFetchReplyCommentsQuery = (
  payload: ReplyCommentsQueryPayload,
  options?: QueryObserverOptions<
    AxiosResponse<ReplyCommentsQueryResponse>,
    AxiosErrorResponse,
    ReplyCommentsQueryResponse,
    AxiosResponse<ReplyCommentsQueryResponse>,
    (string | ReplyCommentsQueryPayload)[]
  >
) => {
  return useQuery([QueryKey.ReelReplyComment, payload], () => fetchReplyComments(payload.commentId, payload.params), options);
};

export const useCreateComment = (
  options?: MutationOptions<AxiosResponse<VideoReelCommentModel>, AxiosErrorResponse, CreateCommentPayload, unknown>
) => {
  return useMutation((payload) => createComment(payload), options);
};

export const useUpdateComment = (
  options?: MutationOptions<AxiosResponse<VideoReelCommentModel>, AxiosErrorResponse, UpdateCommentPayload, unknown>
) => {
  return useMutation((payload) => updateComment(payload), options);
};

export const useDeleteComment = (
  options?: MutationOptions<AxiosResponse<any>, AxiosErrorResponse, number, unknown>
) => {
  return useMutation((payload) => deleteComment(payload), options);
};

export const useCreateReplyComment = (
  options?: MutationOptions<AxiosResponse<VideoReelCommentModel>, AxiosErrorResponse, CreateReplyCommentPayload, unknown>
) => {
  return useMutation((payload) => createReplyComment(payload), options);
};

export const useUpdateReplyComment = (
  options?: MutationOptions<AxiosResponse<VideoReelCommentModel>, AxiosErrorResponse, UpdateReplyCommentPayload, unknown>
) => {
  return useMutation((payload) => updateReplyComment(payload), options);
};

export const useDeleteReplyComment = (
  options?: MutationOptions<AxiosResponse<any>, AxiosErrorResponse, number, unknown>
) => {
  return useMutation((payload) => deleteReplyComment(payload), options);
};

export const useCreateLike = (
  options?: MutationOptions<AxiosResponse<any>, AxiosErrorResponse, any, unknown>
) => {
  return useMutation((payload: { reelId: number, body: any }) => createLike(payload.reelId, payload.body), options);
};

export const useDeleteLike = (
  options?: MutationOptions<AxiosResponse<any>, AxiosErrorResponse, number, unknown>
) => {
  return useMutation((payload) => deleteLike(payload), options);
};

export const useTrackReel = (
  reelId: number,
  options?: any
) => {
  return useQuery([QueryKey.ReelTracking, reelId], () => trackReel(reelId, options));
};